import { useEffect, useRef } from "react";
import type KonvaCore from "konva";
import * as Konva from "react-konva";

import { getPalette, INACTIVE_OPACITY } from "./palette";
import type { SimPick, SimTask, WarehouseLayout } from "./simulator-model";

const FONT_FAMILY = "Public Sans, sans-serif";
const CELL_WIDTH = 60;
const CELL_HEIGHT = 36;
const MARKER_COL_WIDTH = 18;
const BLOCK_WIDTH = CELL_WIDTH + MARKER_COL_WIDTH;
const ROW_GAP = 8;
const COL_GAP = 12;
const MAX_ROWS = 15;
const CORNER_RADIUS = 4;
const MARKER_RADIUS = 5;

export const PALLETS_PER_FORKLIFT = 3;

export const MIN_SCALE = 0.5;
export const MAX_SCALE = 4;
const PANE_PAD = 16;
const MIN_OVERLAP = 64;
const FOLLOW_VISIBLE_CELLS_PER_SIDE = 4;

export type ViewState = { scale: number; x: number; y: number };

type Placed = {
  slot: string;
  pickSequence: number;
  row: number;
  col: number;
};

export type ComputedWarehouseLayout = {
  cells: (Placed | null)[];
  bySlot: Map<string, Placed>;
  rows: number;
  cols: number;
  worldWidth: number;
  worldHeight: number;
};

// Stable, data-driven layout: rows × cols come from the slot count alone, so
// the grid keeps the same shape across pane resizes (zoom is a separate
// concern handled by ViewState).
export function computeWarehouseLayout(
  slots: WarehouseLayout["slots"],
): ComputedWarehouseLayout {
  if (slots.length === 0) {
    return {
      cells: [],
      bySlot: new Map(),
      rows: 1,
      cols: 1,
      worldWidth: BLOCK_WIDTH,
      worldHeight: CELL_HEIGHT,
    };
  }

  let minSeq = Infinity;
  let maxSeq = -Infinity;
  for (const s of slots) {
    if (s.pickSequence < minSeq) minSeq = s.pickSequence;
    if (s.pickSequence > maxSeq) maxSeq = s.pickSequence;
  }
  const seqSpan = maxSeq - minSeq + 1;

  const rows = Math.max(1, Math.min(MAX_ROWS, seqSpan));
  const cols = Math.max(1, Math.ceil(seqSpan / rows));
  const total = rows * cols;

  const cells: (Placed | null)[] = Array.from({ length: total }, () => null);
  const bySlot = new Map<string, Placed>();

  for (const s of slots) {
    const idx = s.pickSequence - minSeq;
    if (idx < 0 || idx >= total) continue;
    const row = idx % rows;
    const col = Math.floor(idx / rows);
    const p: Placed = { slot: s.slot, pickSequence: s.pickSequence, row, col };
    cells[idx] = p;
    bySlot.set(s.slot, p);
  }

  const worldWidth = cols * BLOCK_WIDTH + Math.max(0, cols - 1) * COL_GAP;
  const worldHeight = rows * CELL_HEIGHT + Math.max(0, rows - 1) * ROW_GAP;

  return { cells, bySlot, rows, cols, worldWidth, worldHeight };
}

export function fitView(
  paneWidth: number,
  paneHeight: number,
  worldWidth: number,
  worldHeight: number,
): ViewState {
  if (
    paneWidth <= 0 ||
    paneHeight <= 0 ||
    worldWidth <= 0 ||
    worldHeight <= 0
  ) {
    return { scale: 1, x: 0, y: 0 };
  }
  const usableW = Math.max(1, paneWidth - 2 * PANE_PAD);
  const usableH = Math.max(1, paneHeight - 2 * PANE_PAD);
  // Auto-fit is the baseline scale that makes the grid fit the binding
  // dimension; we don't clamp it by MIN/MAX_SCALE because those are for
  // user-driven zoom. A grid with very wide pick_sequence range will
  // legitimately fit below MIN_SCALE; the user can wheel in afterwards.
  const scale = Math.min(usableW / worldWidth, usableH / worldHeight);
  return {
    scale,
    x: (paneWidth - worldWidth * scale) / 2,
    y: (paneHeight - worldHeight * scale) / 2,
  };
}

// Follow target: centre the active cell's block (slot rect + pallet markers)
// in the pane at a zoom where ~4 cells fit per side. Clamped to the manual
// zoom range so Follow reads as a user-facing zoom level.
export function computeFollowView(
  paneWidth: number,
  paneHeight: number,
  row: number,
  col: number,
): ViewState {
  if (paneWidth <= 0 || paneHeight <= 0) {
    return { scale: 1, x: 0, y: 0 };
  }
  const cellPitch = Math.max(BLOCK_WIDTH + COL_GAP, CELL_HEIGHT + ROW_GAP);
  const binding = Math.min(paneWidth, paneHeight);
  const target = binding / (cellPitch * FOLLOW_VISIBLE_CELLS_PER_SIDE);
  const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, target));
  const centerX = col * (BLOCK_WIDTH + COL_GAP) + BLOCK_WIDTH / 2;
  const centerY = row * (CELL_HEIGHT + ROW_GAP) + CELL_HEIGHT / 2;
  return {
    scale,
    x: paneWidth / 2 - centerX * scale,
    y: paneHeight / 2 - centerY * scale,
  };
}

export function zoomAt(
  view: ViewState,
  localX: number,
  localY: number,
  factor: number,
): ViewState {
  const newScale = Math.max(
    MIN_SCALE,
    Math.min(MAX_SCALE, view.scale * factor),
  );
  if (newScale === view.scale) return view;
  const worldX = (localX - view.x) / view.scale;
  const worldY = (localY - view.y) / view.scale;
  return {
    scale: newScale,
    x: localX - worldX * newScale,
    y: localY - worldY * newScale,
  };
}

export function clampViewToPane(
  view: ViewState,
  paneWidth: number,
  paneHeight: number,
  worldWidth: number,
  worldHeight: number,
): ViewState {
  const w = worldWidth * view.scale;
  const h = worldHeight * view.scale;
  // Keep at least MIN_OVERLAP px of the grid inside the pane on every side.
  const minX = Math.min(MIN_OVERLAP - w, paneWidth - MIN_OVERLAP);
  const maxX = Math.max(MIN_OVERLAP - w, paneWidth - MIN_OVERLAP);
  const minY = Math.min(MIN_OVERLAP - h, paneHeight - MIN_OVERLAP);
  const maxY = Math.max(MIN_OVERLAP - h, paneHeight - MIN_OVERLAP);
  return {
    scale: view.scale,
    x: Math.max(minX, Math.min(maxX, view.x)),
    y: Math.max(minY, Math.min(maxY, view.y)),
  };
}

export type WarehouseProps = {
  layout: ComputedWarehouseLayout;
  activeTask?: SimTask;
  activePick?: SimPick;
  x: number;
  y: number;
  width: number;
  height: number;
  view: ViewState;
  onViewChange: (next: ViewState) => void;
  onUserAdjust: () => void;
};

export function Warehouse({
  layout,
  activeTask,
  activePick,
  x,
  y,
  width,
  height,
  view,
  onViewChange,
  onUserAdjust,
}: WarehouseProps) {
  const palette = getPalette();
  const { cells, bySlot, rows, worldWidth, worldHeight } = layout;

  const cellOriginX = (col: number) => col * (BLOCK_WIDTH + COL_GAP);
  const cellOriginY = (row: number) => row * (CELL_HEIGHT + ROW_GAP);

  const activeSlots = new Set(activeTask?.slotsWalkOrder ?? []);
  const activePlaced =
    activePick && bySlot.has(activePick.slot)
      ? bySlot.get(activePick.slot)!
      : null;
  const activePalletIdx =
    activeTask && activePick
      ? activeTask.pallets.indexOf(activePick.palletKey)
      : -1;
  const taskPalletCount = activeTask?.pallets.length ?? 0;

  const handleWheel = (e: KonvaCore.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const localX = pointer.x - x;
    const localY = pointer.y - y;
    const factor = Math.exp(-e.evt.deltaY * 0.0015);
    const next = clampViewToPane(
      zoomAt(view, localX, localY, factor),
      width,
      height,
      worldWidth,
      worldHeight,
    );
    onUserAdjust();
    onViewChange(next);
  };

  type DragState = {
    startClientX: number;
    startClientY: number;
    startViewX: number;
    startViewY: number;
    handleMove: (ev: MouseEvent) => void;
    handleUp: (ev: MouseEvent) => void;
    container: HTMLDivElement;
  };
  const dragRef = useRef<DragState | null>(null);

  // Clean up an in-flight drag if the component unmounts mid-gesture so we
  // don't leak window listeners.
  useEffect(() => {
    return () => {
      const drag = dragRef.current;
      if (drag) {
        window.removeEventListener("mousemove", drag.handleMove);
        window.removeEventListener("mouseup", drag.handleUp);
        drag.container.style.cursor = "";
        dragRef.current = null;
      }
    };
  }, []);

  const handleMouseDown = (e: KonvaCore.KonvaEventObject<MouseEvent>) => {
    if (e.evt.button !== 0) return;
    if (dragRef.current) return;
    const stage = e.target.getStage();
    if (!stage) return;
    e.evt.preventDefault();
    onUserAdjust();
    const container = stage.container();
    container.style.cursor = "grabbing";

    const startScale = view.scale;
    const startViewX = view.x;
    const startViewY = view.y;

    const handleMove = (ev: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = ev.clientX - drag.startClientX;
      const dy = ev.clientY - drag.startClientY;
      const next = clampViewToPane(
        {
          scale: startScale,
          x: drag.startViewX + dx,
          y: drag.startViewY + dy,
        },
        width,
        height,
        worldWidth,
        worldHeight,
      );
      onViewChange(next);
    };
    const handleUp = () => {
      const drag = dragRef.current;
      if (!drag) return;
      window.removeEventListener("mousemove", drag.handleMove);
      window.removeEventListener("mouseup", drag.handleUp);
      drag.container.style.cursor = "";
      dragRef.current = null;
    };

    dragRef.current = {
      startClientX: e.evt.clientX,
      startClientY: e.evt.clientY,
      startViewX,
      startViewY,
      handleMove,
      handleUp,
      container,
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  const handleMouseEnter = (e: KonvaCore.KonvaEventObject<MouseEvent>) => {
    if (dragRef.current) return;
    const stage = e.target.getStage();
    if (stage) stage.container().style.cursor = "grab";
  };

  const handleMouseLeave = (e: KonvaCore.KonvaEventObject<MouseEvent>) => {
    if (dragRef.current) return;
    const stage = e.target.getStage();
    if (stage) stage.container().style.cursor = "";
  };

  return (
    <Konva.Group
      x={x}
      y={y}
      clipX={0}
      clipY={0}
      clipWidth={width}
      clipHeight={height}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Pane-wide hit surface so wheel + drag start anywhere in the pane,
          including the empty area outside the (transformed) grid bounds. */}
      <Konva.Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="transparent"
      />
      <Konva.Group
        x={view.x}
        y={view.y}
        scaleX={view.scale}
        scaleY={view.scale}
      >
        {cells.map((p, idx) => {
          const row = idx % rows;
          const col = Math.floor(idx / rows);
          if (p == null) {
            return (
              <Konva.Rect
                key={`empty-${idx}`}
                x={cellOriginX(col)}
                y={cellOriginY(row)}
                width={CELL_WIDTH}
                height={CELL_HEIGHT}
                stroke={palette.emptyStroke}
                strokeWidth={1}
                strokeScaleEnabled={false}
                cornerRadius={CORNER_RADIUS}
                opacity={INACTIVE_OPACITY}
              />
            );
          }
          const isActivePick = activePlaced?.slot === p.slot;
          const inActiveTask = activeSlots.has(p.slot);
          return (
            <Konva.Group
              key={p.slot}
              x={cellOriginX(p.col)}
              y={cellOriginY(p.row)}
            >
              <FilledSlot
                cell={p}
                isActivePick={isActivePick}
                inActiveTask={inActiveTask}
                activePalletIdx={isActivePick ? activePalletIdx : -1}
                taskPalletCount={taskPalletCount}
              />
            </Konva.Group>
          );
        })}
      </Konva.Group>
    </Konva.Group>
  );
}

type FilledSlotProps = {
  cell: { slot: string; pickSequence: number };
  isActivePick: boolean;
  inActiveTask: boolean;
  activePalletIdx: number;
  taskPalletCount: number;
};

function FilledSlot({
  cell,
  isActivePick,
  inActiveTask,
  activePalletIdx,
  taskPalletCount,
}: FilledSlotProps) {
  const palette = getPalette();
  const dim = !(inActiveTask || isActivePick);
  const stroke = isActivePick
    ? palette.activeStroke
    : inActiveTask
      ? palette.inTaskStroke
      : palette.cellStroke;
  const strokeWidth = isActivePick ? palette.activeStrokeWidth : 1;
  const fill = isActivePick ? palette.activeFill : undefined;

  return (
    <Konva.Group opacity={dim ? INACTIVE_OPACITY : 1}>
      <Konva.Rect
        x={0}
        y={0}
        width={CELL_WIDTH}
        height={CELL_HEIGHT}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeScaleEnabled={false}
        fill={fill}
        cornerRadius={CORNER_RADIUS}
      />
      <Konva.Text
        x={6}
        y={6}
        text={cell.slot}
        fontSize={10}
        fontFamily={FONT_FAMILY}
        fontStyle={isActivePick ? "bold" : "normal"}
        fill={palette.textTitle}
        width={CELL_WIDTH - 12}
        ellipsis
        wrap="none"
      />
      <Konva.Text
        x={6}
        y={20}
        text={`seq ${cell.pickSequence}`}
        fontSize={9}
        fontFamily={FONT_FAMILY}
        fill={palette.textMuted}
        width={CELL_WIDTH - 12}
        ellipsis
        wrap="none"
      />
      <PalletMarkers
        isInActiveTask={inActiveTask || isActivePick}
        activePalletIdx={activePalletIdx}
        taskPalletCount={taskPalletCount}
      />
    </Konva.Group>
  );
}

type PalletMarkersProps = {
  isInActiveTask: boolean;
  activePalletIdx: number;
  taskPalletCount: number;
};

function PalletMarkers({
  isInActiveTask,
  activePalletIdx,
  taskPalletCount,
}: PalletMarkersProps) {
  const palette = getPalette();
  const total = PALLETS_PER_FORKLIFT;
  const centerX = CELL_WIDTH + MARKER_COL_WIDTH / 2;
  const ys = [10, 22, 34];

  return (
    <>
      {Array.from({ length: total }, (_, idx) => {
        let fill = palette.markerUnused;
        let opacity = 1;
        if (!isInActiveTask) {
          fill = palette.markerInactive;
          opacity = INACTIVE_OPACITY;
        } else if (idx === activePalletIdx) {
          fill = palette.markerActive;
        } else if (idx < taskPalletCount) {
          fill = palette.markerInTask;
        }
        return (
          <Konva.Circle
            key={idx}
            x={centerX}
            y={ys[idx]}
            radius={MARKER_RADIUS}
            fill={fill}
            opacity={opacity}
          />
        );
      })}
    </>
  );
}
